// LivePointsTracker — standalone version that reads from TripBuilderContext
// (manual picking flow). Mirrors the PointsBudgetTracker inside AutoPlanner
// so the user sees their points spend update live on both surfaces.
//
// Picks from: tb.flight (legacy single), tb.flightsByLeg (multi-city per-leg),
// tb.hotel (primary stop), tb.additionalStops[].hotel.

import { useTripBuilder } from '../TripBuilderContext';

export default function LivePointsTracker() {
  const tb = useTripBuilder();
  const startingBalances = {
    amex_mr: tb.amexTotal || 0,
    chase_ur: tb.chaseTotal || 0,
  };
  const travelers = tb.travelers || 2;

  let amexSpent = 0, chaseSpent = 0, cashSpent = 0;

  // Legacy single flight
  if (tb.flight) {
    const pts = (tb.flight.total_points_two_travelers || 0);
    const src = (tb.flight.source_program || 'amex_mr').toLowerCase();
    cashSpent += tb.flight.total_estimated_taxes || 0;
    if (src.includes('amex')) amexSpent += pts; else chaseSpent += pts;
  }
  // Per-leg flights
  Object.values(tb.flightsByLeg || {}).forEach(f => {
    if (!f) return;
    const pts = f.total_points_two_travelers || 0;
    const src = (f.source_program || 'amex_mr').toLowerCase();
    cashSpent += f.total_estimated_taxes || 0;
    if (src.includes('amex')) amexSpent += pts; else chaseSpent += pts;
  });
  // Primary hotel
  if (tb.hotel) {
    const pts = tb.hotel.total_points || 0;
    const src = (tb.hotel.source_program || 'chase_ur').toLowerCase();
    if (src.includes('amex')) amexSpent += pts; else chaseSpent += pts;
    if (!pts && tb.hotel.cash_price_per_night) cashSpent += (tb.hotel.nights || 3) * tb.hotel.cash_price_per_night;
  }
  // Additional stop hotels
  (tb.additionalStops || []).forEach(s => {
    if (!s.hotel) return;
    const pts = s.hotel.total_points || 0;
    const src = (s.hotel.source_program || 'chase_ur').toLowerCase();
    if (src.includes('amex')) amexSpent += pts; else chaseSpent += pts;
    if (!pts && s.hotel.cash_price_per_night) cashSpent += (s.hotel.nights || 3) * s.hotel.cash_price_per_night;
  });

  // Scale activities + restaurants to traveler count (rough cash add)
  (tb.activities || []).forEach(a => { if (a.price_for_two) cashSpent += a.price_for_two * (travelers / 2); });
  (tb.restaurants || []).forEach(r => { if (r.avg_meal_for_two) cashSpent += r.avg_meal_for_two * (travelers / 2); });
  (tb.additionalStops || []).forEach(s => {
    (s.activities || []).forEach(a => { if (a.price_for_two) cashSpent += a.price_for_two * (travelers / 2); });
    (s.restaurants || []).forEach(r => { if (r.avg_meal_for_two) cashSpent += r.avg_meal_for_two * (travelers / 2); });
  });

  const rows = [
    { label: 'Amex MR', spent: amexSpent, balance: startingBalances.amex_mr },
    { label: 'Chase UR', spent: chaseSpent, balance: startingBalances.chase_ur },
  ];

  if (startingBalances.amex_mr === 0 && startingBalances.chase_ur === 0 && cashSpent === 0) return null;

  return (
    <div className="bg-navy-light border border-teal/30 rounded-xl p-2.5 mb-3">
      <div className="text-[9px] font-bold text-teal uppercase tracking-wider mb-1.5">💸 Live spend tracker</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
        {rows.filter(r => r.balance > 0 || r.spent > 0).map(r => {
          const remaining = r.balance - r.spent;
          const over = remaining < 0;
          const tight = !over && r.balance > 0 && r.spent / r.balance > 0.8;
          return (
            <div key={r.label} className="bg-brand-ink/5 rounded p-1.5">
              <div className="text-[9px] uppercase font-bold text-brand-ink/55">{r.label}</div>
              <div className={`text-[11px] font-bold mt-0.5 ${over ? 'text-rose-300' : tight ? 'text-amber-300' : 'text-brand-ink'}`}>
                {r.spent.toLocaleString()} <span className="text-brand-ink/45">/ {r.balance.toLocaleString()}</span>
              </div>
              <div className={`text-[9px] ${over ? 'text-rose-300 font-bold' : 'text-brand-ink/60'}`}>
                {remaining >= 0 ? `${remaining.toLocaleString()} left` : `${Math.abs(remaining).toLocaleString()} over`}
              </div>
            </div>
          );
        })}
        {cashSpent > 0 && (
          <div className="bg-brand-ink/5 rounded p-1.5">
            <div className="text-[9px] uppercase font-bold text-brand-ink/55">Est. cash</div>
            <div className="text-[11px] font-bold mt-0.5 text-brand-ink">${cashSpent.toLocaleString()}</div>
            <div className="text-[9px] text-brand-ink/60">taxes + meals + activities</div>
          </div>
        )}
      </div>
    </div>
  );
}
